import {
  useReactTable,
  getCoreRowModel,
  flexRender,
  createColumnHelper,
} from '@tanstack/react-table';
import type { MonthlyBillWorker } from '@/types/admin.types';
import { Theme } from '@/styles/Theme';
import styled from '@emotion/styled';
import Text from '@/components/common/Text';

const formatDate = (dateString: string) => {
  const date = new Date(dateString);
  return date.toLocaleDateString('ko-KR', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
};

const formatAmount = (amount: number) => {
  return `${amount.toLocaleString('ko-KR')}원`;
};

const columnHelper = createColumnHelper<MonthlyBillWorker>();

const columns = [
  columnHelper.accessor('worker_name', {
    header: '작업자명',
    cell: (info) => info.getValue() || '-',
    size: 200,
  }),
  columnHelper.accessor('active_from', {
    header: '활성 시작일',
    cell: (info) => formatDate(info.getValue()),
    size: 150,
  }),
  columnHelper.accessor('active_to', {
    header: '활성 종료일',
    cell: (info) => (info.getValue() ? formatDate(info.getValue()!) : '-'),
    size: 150,
  }),
  columnHelper.accessor('days_active', {
    header: '활성 일수',
    cell: (info) => `${info.getValue()}일`,
    size: 100,
  }),
  columnHelper.accessor('price_per_worker', {
    header: '작업자당 가격',
    cell: (info) => formatAmount(info.getValue()),
    size: 150,
  }),
  columnHelper.accessor('amount', {
    header: '금액',
    cell: (info) => formatAmount(info.getValue()),
    size: 120,
  }),
];

interface WorkersTableProps {
  workers: MonthlyBillWorker[];
}

const WorkersTable = ({ workers }: WorkersTableProps) => {
  const { getHeaderGroups, getRowModel } = useReactTable({
    data: workers,
    columns,
    getCoreRowModel: getCoreRowModel(),
  });

  if (workers.length === 0) {
    return (
      <Container>
        <Text fontSize='1.25rem' fontWeight={700} margin='0 0 1rem 0'>
          작업자 목록
        </Text>
        <EmptyText>작업자가 없습니다.</EmptyText>
      </Container>
    );
  }

  return (
    <Container>
      <Text fontSize='1.25rem' fontWeight={700} margin='0 0 1rem 0'>
        작업자 목록 ({workers.length}명)
      </Text>
      <TableContainer>
        <Table>
          <thead>
            {getHeaderGroups().map((headerGroup) => (
              <Tr key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <Th key={header.id} style={{ width: header.getSize() }}>
                    {flexRender(header.column.columnDef.header, header.getContext())}
                  </Th>
                ))}
              </Tr>
            ))}
          </thead>
          <tbody>
            {getRowModel().rows.map((row) => (
              <Tr key={row.id}>
                {row.getVisibleCells().map((cell) => (
                  <Td key={cell.id}>
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </Td>
                ))}
              </Tr>
            ))}
          </tbody>
        </Table>
      </TableContainer>
    </Container>
  );
};

const Container = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.1);
`;

const TableContainer = styled.div`
  overflow-x: auto;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: auto;
`;

const Tr = styled.tr`
  border-bottom: 0.0625rem solid ${Theme.colors.G_4};
  
  &:hover {
    background-color: ${Theme.colors.G_6};
  }
`;

const Th = styled.th`
  padding: 0.75rem;
  text-align: left;
  font-size: 0.875rem;
  font-weight: 600;
  color: ${Theme.colors.G_1};
  background-color: ${Theme.colors.G_6};
`;

const Td = styled.td`
  padding: 0.75rem;
  font-size: 0.875rem;
  color: ${Theme.colors.G_1};
`;

const EmptyText = styled(Text)`
  text-align: center;
  padding: 2rem;
  color: ${Theme.colors.G_2};
`;

export default WorkersTable;

